<template>
  <LayoutAdmin :active-periode="activePeriodic">
    <section class="md:px-8 px-4 mt-6 w-full">
      <section
        class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6"
      >
        <div class="flex flex-col gap-2">
          <h1 class="text-lg md:text-2xl">Account Settings</h1>
          <p class="text-sm text-gray-500">
            Kelola akun berdasarkan kategori agar data admins, companies, dan
            employments terpisah dengan lebih rapi tanpa filter per company.
          </p>
        </div>
      </section>

      <section class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          class="text-left rounded-xl border p-4 transition shadow-sm"
          :class="
            activeTab === tab.value
              ? 'border-primary bg-blue-50'
              : 'border-gray-200 bg-white hover:border-primary/40'
          "
          @click="activeTab = tab.value"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-base font-semibold text-gray-800">
                {{ tab.label }}
              </p>
              <p class="text-sm text-gray-500 mt-1">
                {{ tab.description }}
              </p>
            </div>
            <span
              class="min-w-[40px] h-10 px-3 rounded-lg flex items-center justify-center text-sm font-semibold"
              :class="
                activeTab === tab.value
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700'
              "
            >
              {{ activeTab === tab.value ? accounts.length : "-" }}
            </span>
          </div>
        </button>
      </section>

      <section
        class="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
      >
        <section
          class="border-b px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-gray-50"
        >
          <div>
            <h2 class="text-base md:text-lg font-semibold text-gray-800">
              {{ activeTabConfig.label }}
            </h2>
            <p class="text-sm text-gray-500 mt-1">
              {{ activeTabConfig.helper }}
            </p>
          </div>
          <div class="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
            <span class="px-3 py-1 rounded-full bg-white border border-gray-200">
              Total {{ accounts.length }} data
            </span>
          </div>
        </section>

        <section class="px-4 md:px-6 pt-4">
          <div class="flex flex-wrap gap-2">
            <button
              v-for="tab in tabs"
              :key="tab.value"
              class="px-4 py-2 rounded-lg text-sm font-medium transition"
              :class="
                activeTab === tab.value
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600'
              "
              @click="activeTab = tab.value"
            >
              {{ tab.label }}
            </button>
          </div>
        </section>

        <section class="overflow-x-auto custom-scrollbar min-h-[320px] pt-4">
          <table class="w-full text-sm">
            <thead class="bg-gray-50 text-left text-gray-500">
              <tr>
                <th class="px-4 md:px-6 py-3">Status</th>
                <th class="px-4 md:px-6 py-3">Email</th>
                <th class="px-4 md:px-6 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody v-if="!loading.list && accounts.length > 0">
              <tr
                v-for="account in accounts"
                :key="account._id"
                class="border-t align-top"
              >
                <template v-if="activeTab === 'admins'">
                  <td class="px-4 md:px-6 py-4">
                    <span
                      class="px-2 py-1 rounded text-xs font-medium"
                      :class="statusClass(account.status)"
                    >
                      {{ account.status ? "Active" : "Inactive" }}
                    </span>
                  </td>
                  <td class="px-4 md:px-6 py-4">{{ account.email || "-" }}</td>
                </template>

                <template v-else-if="activeTab === 'companies'">
                  <td class="px-4 md:px-6 py-4">
                    <span
                      class="px-2 py-1 rounded text-xs font-medium"
                      :class="statusClass(account.company_status)"
                    >
                      {{ account.company_status ? "Active" : "Inactive" }}
                    </span>
                  </td>
                  <td class="px-4 md:px-6 py-4">
                    {{ account.company_email || "-" }}
                  </td>
                </template>

                <template v-else>
                  <td class="px-4 md:px-6 py-4">
                    <span
                      class="px-2 py-1 rounded text-xs font-medium"
                      :class="employmentStatusClass(account)"
                    >
                      {{ account.emp_employment_status ? "Active" : "Inactive" }}
                    </span>
                  </td>
                  <td class="px-4 md:px-6 py-4">{{ account.email || "-" }}</td>
                </template>

                <td class="px-4 md:px-6 py-4 text-right">
                  <Button
                    class="bg-primary text-white rounded px-4 py-2 text-xs inline-flex"
                    @click="openPasswordModal(account)"
                  >
                    Update Password
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>

          <Loading v-if="loading.list" />
          <NoDataShowing v-if="!loading.list && accounts.length === 0" />
        </section>
      </section>
    </section>
  </LayoutAdmin>

  <Modal
    title="Update Password"
    :showModal="modal.show"
    modalClass="md:w-5/12 sm:w-full"
    @close="closeModal"
  >
    <section class="py-2">
      <div class="mb-5 text-sm text-gray-600">
        <p class="font-medium text-gray-800">{{ selectedAccountLabel }}</p>
        <p>{{ selectedAccountSubLabel }}</p>
      </div>
      <Input
        :value="modal.password"
        @input="modal.password = $event"
        type="password"
        label="New Password"
        class="flex-col"
        label_class="w-full"
        input_class="mt-2"
      />
      <Input
        :value="modal.confirmPassword"
        @input="modal.confirmPassword = $event"
        type="password"
        label="Confirm Password"
        class="flex-col"
        label_class="w-full"
        input_class="mt-2"
      />
    </section>
    <template #footer>
      <section class="flex justify-end gap-2 w-full">
        <Button
          class="bg-gray-300 text-gray-700 rounded px-4 py-2"
          @click="closeModal"
        >
          Cancel
        </Button>
        <Button
          class="bg-primary text-white rounded px-4 py-2"
          :disabled="loading.submit"
          @click="submitPassword"
        >
          Save
        </Button>
      </section>
    </template>
  </Modal>
</template>

<script>
import LayoutAdmin from "@/components/Layout/Admin.vue";
import Button from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Input from "@/components/Input.vue";
import Loading from "@/components/Loading.vue";
import NoDataShowing from "@/components/NoDataShowing.vue";
import {
  GetAdminAccountsAPI,
  GetCompanyAccountsAPI,
  GetEmploymentAccountsAPI,
  UpdateAdminPasswordAPI,
  UpdateCompanyPasswordAPI,
  UpdateEmploymentPasswordAPI,
} from "@/actions/account-settings";
import { useToast } from "vue-toastification";

export default {
  name: "AccountSettings",
  components: {
    LayoutAdmin,
    Button,
    Modal,
    Input,
    Loading,
    NoDataShowing,
  },
  data() {
    return {
      activePeriodic: "-",
      activeTab: "admins",
      tabs: [
        {
          label: "Admins",
          value: "admins",
          description: "Akun admin aplikasi dan group",
          helper: "Data admin dipisahkan agar pengelolaan role dan akses lebih jelas.",
        },
        {
          label: "Companies",
          value: "companies",
          description: "Akun login untuk entitas company",
          helper: "Data company dikelompokkan terpisah dari akun admin dan employee.",
        },
        {
          label: "Employments",
          value: "employments",
          description: "Akun employee berdasarkan company",
          helper: "Data employment menampilkan akun user karyawan per company terpilih.",
        },
      ],
      accounts: [],
      errors: {
        list: "",
      },
      loading: {
        list: false,
        submit: false,
      },
      modal: {
        show: false,
        account: null,
        password: "",
        confirmPassword: "",
      },
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  computed: {
    activeTabConfig() {
      return (
        this.tabs.find((tab) => tab.value === this.activeTab) || this.tabs[0]
      );
    },
    selectedAccountLabel() {
      if (!this.modal.account) {
        return "-";
      }
      if (this.activeTab === "employments") {
        return this.modal.account.emp_fullname || "-";
      }
      if (this.activeTab === "admins") {
        return this.modal.account.name || "-";
      }
      return this.modal.account.company_name || "-";
    },
    selectedAccountSubLabel() {
      if (!this.modal.account) {
        return "-";
      }
      if (this.activeTab === "companies") {
        return this.modal.account.company_email || "-";
      }
      return this.modal.account.email || "-";
    },
  },
  methods: {
    statusClass(status) {
      return status
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700";
    },
    employmentStatusClass(account) {
      return account.emp_employment_status
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700";
    },
    closeModal() {
      this.modal.show = false;
      this.modal.account = null;
      this.modal.password = "";
      this.modal.confirmPassword = "";
    },
    openPasswordModal(account) {
      this.modal.show = true;
      this.modal.account = account;
    },
    async loadAccounts() {
      this.loading.list = true;
      this.errors.list = "";
      let response = null;

      if (this.activeTab === "employments") {
        response = await GetEmploymentAccountsAPI();
      } else if (this.activeTab === "admins") {
        response = await GetAdminAccountsAPI();
      } else {
        response = await GetCompanyAccountsAPI();
      }

      if (response?.status === 401) {
        this.$router.push("/login");
        this.$store.commit("changeIsLoggedIn", false);
        this.loading.list = false;
        return;
      }

      if (Array.isArray(response?.data)) {
        this.accounts = response.data;
      } else {
        this.accounts = [];
        this.errors.list = response?.data?.message || "Invalid accounts response";
      }

      if (response?.status >= 400 && this.errors.list) {
        this.toast.error(this.errors.list);
      }

      this.loading.list = false;
    },
    async submitPassword() {
      if (!this.modal.account?._id) {
        return;
      }
      if (this.modal.password.length < 6) {
        this.toast.error("Password minimal 6 karakter");
        return;
      }
      if (this.modal.password !== this.modal.confirmPassword) {
        this.toast.error("Konfirmasi password tidak sama");
        return;
      }

      this.loading.submit = true;
      let response = null;

      if (this.activeTab === "employments") {
        response = await UpdateEmploymentPasswordAPI(
          this.modal.account._id,
          this.modal.password
        );
      } else if (this.activeTab === "admins") {
        response = await UpdateAdminPasswordAPI(
          this.modal.account._id,
          this.modal.password
        );
      } else {
        response = await UpdateCompanyPasswordAPI(
          this.modal.account._id,
          this.modal.password
        );
      }

      if (response?.status === 200) {
        this.toast.success(response?.data?.message || "Password updated");
        this.closeModal();
        await this.loadAccounts();
      } else {
        this.toast.error(response?.data?.message || "Failed to update password");
      }

      this.loading.submit = false;
    },
  },
  watch: {
    activeTab() {
      this.closeModal();
      this.loadAccounts();
    },
  },
  async mounted() {
    await this.loadAccounts();
  },
};
</script>
